<template>
  <div>
    <exo-table :root="_self">
      <exo-form :root="_self">
        <template v-slot:titleTooltip>
          <exo-help-dialog
            :color="'black--text'"
            :margin="'m-0'"
            title="Sensor Definition"
          >
            A sensor is an object that detects the spacecraft absolute attitude
            or relative attitude compared to a target (Sun, Earth, etc...)
            <br />
            The spacecraft visibility depends mainly on sensor orientation,
            field of view and orbit.
            <v-img
              contain
              :src="require('../../assets/images/fov.png')"
              aspect-ratio="8"
            />
          </exo-help-dialog>
        </template>
        <exo-input-name alignWithParametricField :root="_self" />
        <exo-input-select
          alignWithParametricField
          :root="_self"
          name="sensorType"
        >
          <exo-help-dialog title="Sensor type" margin="mt-6 ml-2">
            The sensor type defines the type of target for each sensor, the
            following sensors are available :
            <br />
            <br />
            <b>Sun sensor </b>: This type of sensor detects the Sun, often with
            a wide field of view. It doesn't work in eclipse.
            <br />
            <br />
            <b>Earth sensor </b>: This type of sensor detects the Earth, often
            with a wide field of view. It doesn't work in eclipse.
            <br />
            <br />
            <b>Infra-red Earth sensor </b>: This type of sensor detects the
            Earth using infra-red, often with a wide field of view. It works in
            eclipse.
            <br />
            <br />
            <b>Star tracker </b>: This type of sensor detects a star pattern,
            often with a reduced field of view. It doesn't work with Sun, Earth
            or Moon in the field of view.
            <br />
            <br />
            <b>Antenna </b>: This type of sensor detects the Earth, it
            corresponds to the TM/TC antenna.
            <br />
            <br />
            <b>GPS </b>: This type of sensor detect the GPS constellation, it
            corresponds to the GPS antenna.
            <br />
            <br />
          </exo-help-dialog>
        </exo-input-select>
        <exo-input-parametric :root="_self" name="fieldOfView">
          <exo-help-dialog
            :color="'white--text'"
            :margin="'m-0'"
            title="Field of view"
          >
            The field of view corresponds to half the angle of the visibility
            cone of the sensor.
            <v-img
              contain
              :src="require('../../assets/images/fov.png')"
              aspect-ratio="8"
            />
          </exo-help-dialog>
        </exo-input-parametric>
        <exo-input-parametric
          :root="_self"
          v-if="isExclusionAngleRequired"
          name="exclusionAngle"
        >
          <exo-help-dialog
            :color="'white--text'"
            :margin="'m-0'"
            title="Exclusion angle"
          >
            The exclusion angle corresponds to the minimum angular distance
            between the sensor main direction and the Sun.
          </exo-help-dialog>
        </exo-input-parametric>
        <exo-card-form label="Direction in spacecraft frame">
          <v-row no-gutters justify="start">
            <v-col cols="3" class="mr-15">
              <exo-input-parametric
                :root="_self"
                name="directionInSpacecraftFrameX"
              />
            </v-col>
            <v-col cols="3" class="mr-15">
              <exo-input-parametric
                :root="_self"
                name="directionInSpacecraftFrameY"
              />
            </v-col>
            <v-col cols="3" class="mr-15">
              <exo-input-parametric
                :root="_self"
                name="directionInSpacecraftFrameZ"
              />
            </v-col>
          </v-row>
        </exo-card-form>
      </exo-form>
    </exo-table>
    <exo-modal-references :root="_self" :modalContent="modalRef" />
  </div>
</template>

<script>
import formMixin from '../../mixins/forms.js'
import analyticalMixin from '../../mixins/analyticalFoms.js'
import fieldsDescription from '../../tools/fieldsDescription.json'
import dataTools from '../../tools/data.js'

export default {
  name: 'analyticalsensor',

  mixins: [formMixin, analyticalMixin],

  data() {
    return {
      tableFields: [
        { value: 'displayedName', text: 'Name', sortable: true },
        {
          value: 'directionInSpacecraftFrameX',
          text:
            'X direction' +
            this.getDefaultUnitLabel('directionInSpacecraftFrameX'),
          sortable: true
        },
        {
          value: 'directionInSpacecraftFrameY',
          text:
            'Y direction' +
            this.getDefaultUnitLabel('directionInSpacecraftFrameY'),
          sortable: true
        },
        {
          value: 'directionInSpacecraftFrameZ',
          text:
            'Z direction' +
            this.getDefaultUnitLabel('directionInSpacecraftFrameZ'),
          sortable: true
        },
        {
          value: 'sensorType',
          text: 'Sensor type' + this.getDefaultUnitLabel('sensorType'),
          sortable: true
        },
        {
          value: 'fieldOfView',
          text: 'Field of view' + this.getDefaultUnitLabel('fieldOfView'),
          sortable: true
        },
        { value: 'actions', text: 'Actions', sortable: false }
      ],
      enumPropertiesList: this.getEnumProperties(),
      modalRef: { content: {} },
      showModal: false,
      inputs: {
        directionInSpacecraftFrameX: this.getDefaultValue(
          'directionInSpacecraftFrameX'
        ),
        directionInSpacecraftFrameY: this.getDefaultValue(
          'directionInSpacecraftFrameY'
        ),
        directionInSpacecraftFrameZ: this.getDefaultValue(
          'directionInSpacecraftFrameZ'
        ),
        sensorType: this.getDefaultValue('sensorType'),
        fieldOfView: this.getDefaultValue('fieldOfView'),
        exclusionAngle: this.getDefaultValue('exclusionAngle'),
        parametricValues: {
          directionInSpacecraftFrameX: this.buildDefaultParametricValue(
            'directionInSpacecraftFrameX'
          ),
          directionInSpacecraftFrameY: this.buildDefaultParametricValue(
            'directionInSpacecraftFrameY'
          ),
          directionInSpacecraftFrameZ: this.buildDefaultParametricValue(
            'directionInSpacecraftFrameZ'
          ),
          exclusionAngle: this.buildDefaultParametricValue('exclusionAngle'),
          fieldOfView: this.buildDefaultParametricValue('fieldOfView')
        }
      }
    }
  },
  computed: {
    isExclusionAngleRequired() {
      return (
        this.inputs.sensorType === 'EARTH_SENSOR' ||
        this.inputs.sensorType === 'IR_EARTH_SENSOR' ||
        this.inputs.sensorType === 'STAR_TRACKER'
      )
    }
  },
  methods: {
    getCurrentFormType() {
      return 'ParametricSensorDefinition'
    },

    getAllPropertiesUnionList() {
      let objectType = this.getCurrentFormType()
      let requiredList = dataTools.getPropertiesList(
        fieldsDescription[objectType],
        null,
        'required'
      )
      let optionalList = dataTools.getPropertiesList(
        fieldsDescription[objectType],
        null,
        'optional'
      )
      return [...new Set(requiredList.concat(optionalList))]
    },

    getFieldState(parentObject, fieldName, required) {
      let fieldState = analyticalMixin.methods.getFieldState.call(
        this,
        parentObject,
        fieldName,
        required
      )
      // Check if exclusion angle is correct
      if (
        JSON.stringify(fieldName).includes('exclusionAngle') &&
        this.isExclusionAngleRequired &&
        this.inputs.fieldOfView > this.inputs.exclusionAngle
      ) {
        fieldState.value = false
        fieldState.message =
          'Sorry, exclusion angle must be above or equal to field of view'
        return fieldState
      }
      // Check if norm is correct
      if (
        JSON.stringify(fieldName).includes('directionInSpacecraftFrame') &&
        this.inputs.directionInSpacecraftFrameX == 0 &&
        this.inputs.directionInSpacecraftFrameY == 0 &&
        this.inputs.directionInSpacecraftFrameZ == 0
      ) {
        fieldState.value = false
        fieldState.message = 'Sorry, the norm of the direction must be above 0.'
        return fieldState
      }
      return fieldState
    },

    getSubTypes(instance) {
      let types = {
        sensorType: instance.sensorType
      }
      return types
    }
  }
}
</script>
